import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessageCircle, Search, Sparkles } from "lucide-react";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import SentimentDot from "../../components/SentimentDot";
import { api } from "../../api/client";

const AdminChatConversationsContent = () => {
    const [params, setParams] = useSearchParams();
    const sentiment = params.get("sentiment") || "";
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState(params.get("q") || "");
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);

    const setSentiment = (v) => {
        const p = new URLSearchParams(params);
        if (v) p.set("sentiment", v); else p.delete("sentiment");
        setParams(p);
    };

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                const q = params.get("q") || "";
                const { data } = await api.get("/admin/chat/conversations", { params: { sentiment, q, page_size: 50 } });
                if (mounted) setData(data);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [sentiment, params]);

    useEffect(() => {
        if (!selectedId) { setDetail(null); return; }
        let m = true;
        api.get(`/admin/chat/conversations/${selectedId}`).then(({ data }) => { if (m) setDetail(data); }).catch(() => {});
        return () => { m = false; };
    }, [selectedId]);

    const labels = [
        { v: "", label: "All", count: data.total },
        { v: "urgent", label: "Urgent" },
        { v: "negative", label: "Negative" },
        { v: "neutral", label: "Neutral" },
        { v: "positive", label: "Positive" },
    ];

    return (
        <div className="grid gap-5 lg:grid-cols-[420px_1fr]">
            <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-1.5">
                    {labels.map((l) => (
                        <button
                            key={l.v || "all"}
                            onClick={() => setSentiment(l.v)}
                            className={`rounded-full border px-3 py-1 text-xs font-medium ${
                                sentiment === l.v ? "border-brand-300 bg-brand-700 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-brand-200"
                            }`}
                            data-testid={`admin-chats-chip-${l.label.toLowerCase()}`}
                        >
                            {l.label} {l.count != null && <span className="ml-1 opacity-70">({l.count})</span>}
                        </button>
                    ))}
                </div>

                <form
                    onSubmit={(e) => { e.preventDefault(); const p = new URLSearchParams(params); if (search.trim()) p.set("q", search.trim()); else p.delete("q"); setParams(p); }}
                    className="relative flex items-center"
                >
                    <Search className="pointer-events-none absolute left-3 h-3.5 w-3.5 text-slate-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by chat title…"
                        className="w-full rounded-full border border-slate-200 bg-white pl-9 pr-3 py-1.5 text-xs outline-none focus:border-brand-400"
                        data-testid="admin-chats-search"
                    />
                </form>

                <div className="space-y-2">
                    {loading ? (
                        <>
                            <div className="h-20 skeleton rounded-xl" />
                            <div className="h-20 skeleton rounded-xl" />
                            <div className="h-20 skeleton rounded-xl" />
                        </>
                    ) : data.items.length === 0 ? (
                        <p className="rounded-xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-500" data-testid="admin-chats-empty">
                            No conversations match.
                        </p>
                    ) : data.items.map((c) => (
                        <button
                            key={c.id}
                            onClick={() => setSelectedId(c.id)}
                            className={`w-full rounded-xl border bg-white p-3 text-left transition-all hover:border-brand-200 ${
                                selectedId === c.id ? "border-brand-300 ring-1 ring-brand-200" : "border-slate-100"
                            }`}
                            data-testid={`admin-chats-row-${c.id}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <p className="line-clamp-1 text-sm font-medium text-slate-900">{c.title || "(untitled)"}</p>
                                    <p className="line-clamp-1 text-xs text-slate-500">
                                        {c.user?.full_name || c.user?.email || "Unknown"} · {c.message_count} msgs
                                    </p>
                                </div>
                                <SentimentDot sentiment={c.sentiment} />
                            </div>
                            {c.last_preview && <p className="mt-1 line-clamp-1 text-xs text-slate-500 italic">"{c.last_preview}"</p>}
                        </button>
                    ))}
                </div>
            </div>

            {/* Detail */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                {!selectedId ? (
                    <div className="flex h-full min-h-[400px] flex-col items-center justify-center p-8 text-center" data-testid="admin-chats-detail-empty">
                        <MessageCircle className="h-10 w-10 text-slate-300" />
                        <p className="mt-2 text-sm text-slate-500">Select a conversation to inspect</p>
                    </div>
                ) : !detail ? (
                    <div className="h-96 skeleton" />
                ) : (
                    <div data-testid="admin-chats-detail">
                        <div className="border-b border-slate-100 p-4">
                            <p className="text-xs text-slate-500">
                                <Sparkles className="mr-1 inline h-3 w-3 text-brand-600" /> AI conversation
                            </p>
                            <h2 className="mt-0.5 text-base font-semibold text-slate-900">{detail.conversation.title}</h2>
                            <p className="text-xs text-slate-500">
                                {detail.user?.full_name || detail.user?.email} · {detail.messages.length} messages
                            </p>
                            {detail.conversation.sentiment && (
                                <div className="mt-2 inline-flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5">
                                    <SentimentDot sentiment={detail.conversation.sentiment} showLabel />
                                    <span className="text-xs text-slate-500">— {detail.conversation.sentiment.reasoning}</span>
                                </div>
                            )}
                        </div>
                        <div className="max-h-[600px] space-y-2.5 overflow-y-auto p-4">
                            {detail.messages.map((m) => {
                                const isUser = m.role === "user";
                                return (
                                    <div key={m.id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                                        <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${
                                            isUser ? "bg-brand-700 text-white" : "bg-slate-100 text-slate-800"
                                        }`}>
                                            <p className="whitespace-pre-wrap break-words">{m.content}</p>
                                            <p className={`mt-1 text-[10px] ${isUser ? "text-white/70" : "text-slate-400"}`}>
                                                {isUser ? "User" : "Assistant"} · {new Date(m.created_at).toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const AdminChatConversations = () => (
    <AdminAuthGuard>
        <AdminLayout title="AI chats" subtitle="Customer conversations with the Health Assistant">
            <AdminChatConversationsContent />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminChatConversations;
